// Canned follow-up notes - firm-wide notes set up in the Canned notes lookup. Each belongs to a follow-up action type (Phoned, Emailed ...), and
// within an action type they are shown in the order of `sequence`. They are loaded by cannedNotesRepository.ts / useCannedNotes.ts.
export interface CannedNote {
    id: string;
    subject: string;
    body: string;
    // the follow-up action type the note is written for (null when it is not tied to one)
    actionTypeGuid: string | null;
    actionTypeName: string;
    // position among the notes of its action type - maintained by dragging rows in the Canned notes lookup grid
    sequence: number;
}

// What the cannedNotes query returns, per note (see cannedNotesRepository.ts)
export interface CannedNoteData {
    guid: string;
    subject: string;
    body: string;
    actionTypeGuid: string | null;
    actionTypeName: string | null;
    sequence: number;
    isActive: boolean;
}

export const mapCannedNotes = (cannedNotes: CannedNoteData[] | null | undefined): CannedNote[] => (cannedNotes || [])
    .filter((cannedNote) => cannedNote.isActive !== false)
    .map((cannedNote) => ({
        id: cannedNote.guid,
        subject: cannedNote.subject,
        body: cannedNote.body,
        actionTypeGuid: cannedNote.actionTypeGuid || null,
        actionTypeName: cannedNote.actionTypeName || '',
        sequence: cannedNote.sequence,
    }));

// Grouped by action type, then by sequence. The group of the selected follow-up action comes first, so its notes are the first ones on offer; the other
// groups follow in the order of their first note's sequence, and notes with no action type come last.
export const sortForActionType = (notes: CannedNote[], selectedActionTypeGuid?: string | null): CannedNote[] => {
    const firstSequenceByActionType = new Map<string, number>();
    notes.forEach((note) => {
        const groupKey = note.actionTypeGuid || '';
        const current = firstSequenceByActionType.get(groupKey);
        if (current === undefined || note.sequence < current) {
            firstSequenceByActionType.set(groupKey, note.sequence);
        }
    });

    const groupRank = (note: CannedNote): number => {
        const groupKey = note.actionTypeGuid || '';
        if (selectedActionTypeGuid && groupKey === selectedActionTypeGuid) {
            return -Infinity;
        }
        return groupKey === '' ? Infinity : firstSequenceByActionType.get(groupKey) as number;
    };

    return [...notes].sort((first, second) => {
        const rankDifference = groupRank(first) - groupRank(second);
        // -Infinity - -Infinity (and Infinity - Infinity) is NaN: those notes are in the same group
        if (rankDifference !== 0 && !Number.isNaN(rankDifference)) {
            return rankDifference;
        }
        if ((first.actionTypeGuid || '') !== (second.actionTypeGuid || '')) {
            // two groups that share a first sequence: keep each group together
            return (first.actionTypeGuid || '').localeCompare(second.actionTypeGuid || '');
        }
        return first.sequence - second.sequence;
    });
};

// Appends after any text already in the notes (never replaces it).
export const appendCannedNote = (existing: string | null | undefined, note: CannedNote): string => {
    const current = (existing || '').replace(/\s+$/, '');
    return current ? `${current}\n\n${note.body}` : note.body;
};
